import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import translate from '../../../commonComponents/translate';
import { displayUserName } from '../../../services/utilityFunctions';
import UserProp from '../../../commonComponents/propTypes/userProp';
import UserTypeNamesProp from '../../../commonComponents/propTypes/userTypeNamesProp';
import TeamStructureLevelSection from './TeamStructureLevelSection';
import EyeIcon from '../../../commonComponents/icons/EyeIcon';
import { ORIENTATION } from '../components/TreeHeaderBarFilters';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';

export function UserCard({
  t,
  avatar,
  user,
  directSellingSettings,
  areDetailsVisible,
  selectedUserDetails,
  setSelectedUserDetails,
  userTypeNames,
  isSponsorTree,
  wrapperValuesClass,
  filters,
  onUnselectUser,
}) {
  const handleSelectUser = useCallback(() => {
    if (selectedUserDetails === user._id) {
      setSelectedUserDetails('');
      return;
    }
    setSelectedUserDetails(user._id);
  }, [selectedUserDetails, user]);

  const displayUserRole = (role, type) => {
    if (role === UserProp.ROLES.ADMIN) {
      return t('role_admin');
    }
    if (type) {
      return userTypeNames[type] || t(`user_type_${type}`);
    }
    return t('role_user');
  };

  return (
    <div className="user-wrapper">
      <div className="image-container">
        <img src={avatar} alt={user.username} />
        <div
          role="presentation"
          className="eye-icon"
          onClick={event => {
            event.stopPropagation();
            handleSelectUser();
          }}
        >
          <EyeIcon />
        </div>
        {areDetailsVisible && (
          <div className="user-va-info">
            <TeamStructureLevelSection
              user={user}
              directSellingSettings={directSellingSettings}
              isSponsorTree={isSponsorTree}
              wrapperValuesClass={wrapperValuesClass}
              filters={filters}
              onUnselectUser={onUnselectUser}
            />
          </div>
        )}
      </div>
      <div className="user-name">
        {/** In user portal we only display username in binary tree - client request */}
        {isSponsorTree ? displayUserName(user) : user.username}
      </div>
      <div className="user-container">
        <span className="user-role">
          {displayUserRole(user.role, user.type)}
        </span>
      </div>
    </div>
  );
}

UserCard.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  avatar: PropTypes.string.isRequired,
  userTypeNames: PropTypes.shape(UserTypeNamesProp.propType).isRequired,
  directSellingSettings: PropTypes.shape({
    VAIntegration: PropTypes.bool.isRequired,
    salesIntegration: PropTypes.bool.isRequired,
  }).isRequired,
  areDetailsVisible: PropTypes.bool,
  setSelectedUserDetails: PropTypes.func,
  selectedUserDetails: PropTypes.func,
  isSponsorTree: PropTypes.bool,
  wrapperValuesClass: PropTypes.string,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
  }),
  onUnselectUser: PropTypes.func.isRequired,
};

UserCard.defaultProps = {
  areDetailsVisible: false,
  setSelectedUserDetails: () => {},
  selectedUserDetails: () => {},
  isSponsorTree: true,
  wrapperValuesClass: '',
  filters: {},
};

function TeamStructureUserCard({
  t,
  level,
  user,
  userTypeNames,
  onClickCard,
  isSelected,
  directSellingSettings,
  areDetailsVisible,
  setSelectedUserDetails,
  selectedUserDetails,
  isSponsorTree,
  wrapperValuesClass,
  filters,
  onUnselectUser,
  isSomeSelectedInLevel,
  orientation = ORIENTATION.HORIZONTAL,
}) {
  const { loggedUser } = useContext(LoggedUserContext);
  const avatar =
    user.image && user.image.thumb ? user.image.thumb : '/images/no_user.png';

  return (
    <button
      type="button"
      id={`teamCard_${level}_${user._id}`}
      className={cx('structure-card', {
        selected: isSelected,
        opacity: !isSelected && filters.search && isSomeSelectedInLevel,
      })}
      onClick={e => {
        e.stopPropagation();
        if (!filters.search) {
          onClickCard(level, user);
        }
      }}
    >
      {filters.search &&
        (isSponsorTree
          ? user.sponsoredBy
          : user.directSelling?.binarySide !== 'top') &&
        (isSponsorTree ? level <= 1 : true) &&
        user._id !== loggedUser._id && (
          <button
            aria-label="arrow-left"
            type="button"
            onClick={e => {
              e.stopPropagation();
              onClickCard(level, user, 'up');
            }}
            className={`button-left-tree ${
              orientation === ORIENTATION.VERTICAL
                ? 'button-left-tree-vertical'
                : ''
            }`}
          >
            <i
              className={`fa fa-arrow-${
                orientation === ORIENTATION.HORIZONTAL ? 'left' : 'up'
              }`}
            />
          </button>
        )}
      <UserCard
        t={t}
        user={user}
        avatar={avatar}
        userTypeNames={userTypeNames}
        directSellingSettings={directSellingSettings}
        areDetailsVisible={areDetailsVisible}
        setSelectedUserDetails={setSelectedUserDetails}
        selectedUserDetails={selectedUserDetails}
        isSponsorTree={isSponsorTree}
        wrapperValuesClass={wrapperValuesClass}
        filters={filters}
        onUnselectUser={onUnselectUser}
      />
      {filters.search && (
        <button
          aria-label="arrow-right"
          type="button"
          onClick={e => {
            e.stopPropagation();
            onClickCard(level, user, 'down');
          }}
          className={`button-right-tree ${
            orientation === ORIENTATION.VERTICAL
              ? 'button-right-tree-vertical'
              : ''
          }`}
        >
          <i
            className={`fa fa-arrow-${
              orientation === ORIENTATION.HORIZONTAL ? 'right' : 'down'
            }`}
          />
        </button>
      )}
    </button>
  );
}

TeamStructureUserCard.propTypes = {
  t: PropTypes.func.isRequired,
  level: PropTypes.number.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  isSelected: PropTypes.bool,
  userTypeNames: PropTypes.shape(UserTypeNamesProp.propType).isRequired,
  onClickCard: PropTypes.func.isRequired,
  directSellingSettings: PropTypes.shape({
    VAIntegration: PropTypes.bool.isRequired,
    salesIntegration: PropTypes.bool.isRequired,
  }).isRequired,
  areDetailsVisible: PropTypes.bool,
  setSelectedUserDetails: PropTypes.func,
  selectedUserDetails: PropTypes.func,
  isSponsorTree: PropTypes.bool,
  wrapperValuesClass: PropTypes.string,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    search: PropTypes.string,
  }),
  onUnselectUser: PropTypes.func.isRequired,
  isSomeSelectedInLevel: PropTypes.bool,
  orientation: PropTypes.string,
};

TeamStructureUserCard.defaultProps = {
  isSelected: false,
  areDetailsVisible: false,
  setSelectedUserDetails: () => {},
  selectedUserDetails: () => {},
  isSponsorTree: true,
  wrapperValuesClass: '',
  filters: {},
  isSomeSelectedInLevel: false,
  orientation: 'horizontal',
};

export default translate(TeamStructureUserCard);
